import { supabase } from '@/services/supabase/supabaseClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface DrawingData {
  id: string;
  type: string; // 'line', 'rect', 'circle', 'hline', 'vline', 'arrow'
  time1: number; // Unix timestamp (chart coordinate)
  price1: number; // Price value (chart coordinate)
  time2: number; // Unix timestamp (chart coordinate)
  price2: number; // Price value (chart coordinate)
  color: string;
  userId: string;
  userName: string;
  timestamp: number;
  groupId: string;
  symbol: string;
  properties?: any;
}

interface ChartSyncCallbacks {
  onDrawingCreate: (drawing: DrawingData) => void;
  onDrawingUpdate: (drawing: DrawingData) => void;
  onDrawingDelete: (drawingId: string) => void;
  onError?: (error: string) => void;
}

export class ChartSyncService {
  private channel: RealtimeChannel | null = null;
  private groupId: string;
  private userId: string;
  private symbol: string;
  private callbacks: ChartSyncCallbacks;
  private pendingOutbound: Map<string, number> = new Map();
  private lastApplied: Map<string, number> = new Map();

  constructor(groupId: string, userId: string, symbol: string, callbacks: ChartSyncCallbacks) {
    this.groupId = groupId;
    this.userId = userId;
    this.symbol = symbol;
    this.callbacks = callbacks;
  }

  /**
   * Subscribe to real-time chart drawing updates
   */
  subscribe(): void {
    const channelName = `chart_drawings:${this.groupId}:${this.symbol}`;

    console.log(`[ChartSync] Subscribing to channel: ${channelName}`);

    this.channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'drawing_create' }, payload => {
        if (payload.payload.userId !== this.userId) {
          console.log('[ChartSync] Received drawing_create from remote user');
          this.callbacks.onDrawingCreate(payload.payload);
        }
      })
      .on('broadcast', { event: 'drawing_update' }, payload => {
        if (payload.payload.userId !== this.userId) {
          console.log('[ChartSync] Received drawing_update from remote user');
          const d = payload.payload as DrawingData;
          const ts = d.timestamp || 0;
          const last = this.lastApplied.get(d.id) || 0;
          if (ts >= last) {
            this.lastApplied.set(d.id, ts);
            this.callbacks.onDrawingUpdate(d);
          }
        }
      })
      .on('broadcast', { event: 'drawing_delete' }, payload => {
        if (payload.payload.userId !== this.userId) {
          console.log('[ChartSync] Received drawing_delete from remote user');
          this.callbacks.onDrawingDelete(payload.payload.id);
        }
      })
      .subscribe(status => {
        console.log(`[ChartSync] Channel status: ${status}`);
        if (status === 'SUBSCRIBED') {
          console.log('✅ [ChartSync] Successfully subscribed to real-time updates');
        }
      });
  }

  /**
   * Broadcast drawing creation to other users
   */
  async broadcastDrawingCreate(drawing: DrawingData): Promise<void> {
    if (!this.channel) {
      console.error('[ChartSync] Cannot broadcast - channel not initialized');
      return;
    }

    try {
      console.log('[ChartSync] Broadcasting drawing_create:', drawing.id);

      await this.channel.send({
        type: 'broadcast',
        event: 'drawing_create',
        payload: drawing,
      });

      // Persist to database for users joining later
      await this.saveDrawingToDatabase(drawing);
    } catch (error) {
      console.error('[ChartSync] Failed to broadcast drawing create:', error);
      this.callbacks.onError?.('Failed to sync drawing');
    }
  }

  /**
   * Broadcast drawing update to other users
   */
  async broadcastDrawingUpdate(drawing: DrawingData): Promise<void> {
    if (!this.channel) return;

    try {
      // throttle outbound updates per id (100ms)
      const now = Date.now();
      const nextAllowed = this.pendingOutbound.get(drawing.id) || 0;
      if (now < nextAllowed) return;
      this.pendingOutbound.set(drawing.id, now + 100);

      console.log('[ChartSync] Broadcasting drawing_update:', drawing.id);

      await this.channel.send({
        type: 'broadcast',
        event: 'drawing_update',
        payload: drawing,
      });

      await this.updateDrawingInDatabase(drawing);
    } catch (error) {
      console.error('[ChartSync] Failed to broadcast drawing update:', error);
    }
  }

  /**
   * Broadcast drawing deletion to other users
   */
  async broadcastDrawingDelete(drawingId: string): Promise<void> {
    if (!this.channel) return;

    try {
      console.log('[ChartSync] Broadcasting drawing_delete:', drawingId);

      await this.channel.send({
        type: 'broadcast',
        event: 'drawing_delete',
        payload: { id: drawingId, userId: this.userId },
      });

      await this.deleteDrawingFromDatabase(drawingId);
    } catch (error) {
      console.error('[ChartSync] Failed to broadcast drawing delete:', error);
    }
  }

  /**
   * Load existing drawings from database
   * @param privateMode - If true, only load drawings from current user
   */
  async loadDrawings(privateMode: boolean = false): Promise<DrawingData[]> {
    try {
      console.log(
        `[ChartSync] Loading drawings for group ${this.groupId}, symbol ${this.symbol}, privateMode: ${privateMode}`
      );

      let query = supabase
        .from('chart_drawings')
        .select('*')
        .eq('group_id', this.groupId)
        .eq('symbol', this.symbol);

      // Filter by userId in private mode
      if (privateMode) {
        query = query.eq('user_id', this.userId);
      }

      const { data, error } = await query.order('created_at', { ascending: true });

      if (error) {
        console.error('[ChartSync] Failed to load drawings:', error);
        return [];
      }

      console.log(`[ChartSync] Loaded ${data?.length || 0} existing drawings`);

      return (data || []).map(row => {
        const points = row.points as any;
        return {
          id: row.id,
          type: row.drawing_type,
          time1: points.time1 || 0,
          price1: points.price1 || 0,
          time2: points.time2 || 0,
          price2: points.price2 || 0,
          color: points.color || '#3B82F6',
          userId: row.user_id,
          userName: row.user_name,
          timestamp: new Date(row.created_at).getTime(),
          groupId: row.group_id,
          symbol: row.symbol,
          properties: row.properties || null,
        };
      });
    } catch (error) {
      console.error('[ChartSync] Unexpected error loading drawings:', error);
      return [];
    }
  }

  /**
   * Save drawing to database
   */
  private async saveDrawingToDatabase(drawing: DrawingData): Promise<void> {
    try {
      const { error } = await supabase.from('chart_drawings').insert({
        id: drawing.id,
        group_id: drawing.groupId,
        user_id: drawing.userId,
        user_name: drawing.userName,
        symbol: drawing.symbol,
        drawing_type: drawing.type,
        points: {
          time1: drawing.time1,
          price1: drawing.price1,
          time2: drawing.time2,
          price2: drawing.price2,
          color: drawing.color,
        },
        properties: drawing.properties || null,
      });

      if (error) {
        console.error('[ChartSync] Failed to save drawing:', error);
      } else {
        console.log('[ChartSync] Drawing saved to database:', drawing.id);
      }
    } catch (error) {
      console.error('[ChartSync] Unexpected error saving drawing:', error);
    }
  }

  /**
   * Update drawing in database
   */
  private async updateDrawingInDatabase(drawing: DrawingData): Promise<void> {
    try {
      const { error } = await supabase
        .from('chart_drawings')
        .update({
          points: {
            time1: drawing.time1,
            price1: drawing.price1,
            time2: drawing.time2,
            price2: drawing.price2,
            color: drawing.color,
          },
          properties: drawing.properties || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', drawing.id);

      if (error) {
        console.error('[ChartSync] Failed to update drawing:', error);
      }
    } catch (error) {
      console.error('[ChartSync] Unexpected error updating drawing:', error);
    }
  }

  /**
   * Delete drawing from database
   */
  private async deleteDrawingFromDatabase(drawingId: string): Promise<void> {
    try {
      const { error } = await supabase.from('chart_drawings').delete().eq('id', drawingId);

      if (error) {
        console.error('[ChartSync] Failed to delete drawing:', error);
      }
    } catch (error) {
      console.error('[ChartSync] Unexpected error deleting drawing:', error);
    }
  }

  /**
   * Save drawing to database only (no broadcast) - for private mode
   */
  async saveDrawingOnly(drawing: DrawingData): Promise<void> {
    try {
      console.log('[ChartSync] Saving drawing (private mode):', drawing.id);
      await this.saveDrawingToDatabase(drawing);
    } catch (error) {
      console.error('[ChartSync] Failed to save drawing:', error);
      this.callbacks.onError?.('Failed to save drawing');
    }
  }

  /**
   * Update drawing in database only (no broadcast) - for private mode
   */
  async updateDrawingOnly(drawing: DrawingData): Promise<void> {
    try {
      console.log('[ChartSync] Updating drawing (private mode):', drawing.id);
      await this.updateDrawingInDatabase(drawing);
    } catch (error) {
      console.error('[ChartSync] Failed to update drawing:', error);
    }
  }

  /**
   * Delete drawing from database only (no broadcast) - for private mode
   */
  async deleteDrawingOnly(drawingId: string): Promise<void> {
    try {
      console.log('[ChartSync] Deleting drawing (private mode):', drawingId);
      await this.deleteDrawingFromDatabase(drawingId);
    } catch (error) {
      console.error('[ChartSync] Failed to delete drawing:', error);
    }
  }

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(): void {
    if (this.channel) {
      console.log('[ChartSync] Unsubscribing from channel');
      supabase.removeChannel(this.channel);
      this.channel = null;
    }
  }
}
